import React, { useEffect, useState } from "react";
import { Stack, Button, Typography, useTheme, MenuItem } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Strings } from "@app/constants";
import {
  FormProvider,
  RHFRadioSingle,
  RHFSelect,
  RHFTextField,
} from "@app/components";
import { useDispatch } from "react-redux";
import { brand } from "@app/modules/categories/slice";
import { useRequestState, useResponsive } from "@app/hooks";
import { selectBrandState } from "@app/modules/categories/selector";
import { PATH_DASHBOARD } from "@app/routes/paths";

const CarSearchBar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isTab = useResponsive("down", "md");
  const isMobile = useResponsive("down", "sm");

  const methods = useForm({
    defaultValues: {
      searchType: "budget",
      budget: "",
      kmDriven: "",
      brand: "",
      model: "",
      carRegNo: "",
    },
  });

  const [isSelected, setIsSelected] = useState(0);
  const { data: brandData } = useRequestState({
    stateSelector: selectBrandState,
  });

  useEffect(() => {
    dispatch(brand());
  }, [dispatch]);

  const searchType = methods.watch("searchType");

  const budgetOptions = Array.from({ length: 10 }, (_, i) => {
    const step = 100000;
    const min = i * step;
    const max = min + step;
    return {
      label: `${min.toLocaleString("en-IN")} - ${max.toLocaleString("en-IN")}`,
      value: `${min}-${max}`,
    };
  });

  const kmDrivenOptions = [
    { label: "0 - 5,000 km", value: "0-5000" },
    { label: "5,000 - 10,000 km", value: "5000-10000" },
    { label: "10,000 - 20,000 km", value: "10000-20000" },
  ];

  const handleSubmitBuy = (values: any) => {
    navigate(PATH_DASHBOARD.productListPage, {
      state: { formParamsValues: values },
    });
  };

  const handleSubmitSell = (values: any) => {
    navigate(PATH_DASHBOARD.sellCar, {
      state: { formValue: values },
    });
  };

  const renderBuyCar = () => {
    const fields =
      searchType === "budget"
        ? [
            { name: "budget", label: "Select Budget", options: budgetOptions },
            { name: "kmDriven", label: "Km Driven", options: kmDrivenOptions },
          ]
        : [
            {
              name: "brand",
              label: "Select Brand",
              options: brandData?.map((b: any) => ({
                label: b.name,
                value: b.name,
              })),
            },
            {
              name: "model",
              label: "Select Model",
              options:
                brandData
                  ?.flatMap((b: any) =>
                    b.models?.map((m: any) => ({
                      label: m.name,
                      value: m.name,
                    })),
                  )
                  .filter(Boolean) || [],
            },
          ];

    return (
      <Stack>
        <Stack flexDirection="row" gap={2} sx={{ zIndex: 2 }}>
          <RHFRadioSingle name="searchType" label="By Budget" value="budget" />
          <RHFRadioSingle name="searchType" label="By Brand" value="brand" />
        </Stack>

        <Stack direction={"column"} spacing={1} mt={1}>
          {fields.map((field, i) => (
            <RHFSelect
              key={i}
              name={field.name}
              placeholder={field.label}
              sx={{ backgroundColor: "white", borderRadius: 1, flex: 1 }}
            >
              {field.options?.map((option: any, idx: number) => (
                <MenuItem key={idx} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
          ))}

          <Button
            variant="contained"
            sx={{
              px: { xs: 4, md: 10, width: "100%" },
              textTransform: "none",
              flexShrink: 0,
            }}
            onClick={methods.handleSubmit(handleSubmitBuy)}
          >
            {Strings.button.search}
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderSellCar = () => (
    <Stack height={"100%"}>
      <Typography sx={{ mb: 1, color: theme.palette.common.black, zIndex: 11 }}>
        {Strings.home.enterCarRegistrationNumber}
      </Typography>
      <Stack
        direction={"column"}
        spacing={1}
        alignItems="center"
        height={"100%"}
      >
        <RHFTextField
          name="carNumber"
          placeholder="(e.g DL34AC4564)"
          style={{
            backgroundColor: theme.palette.common.white,
            opacity: 0.8,
            flex: 0.8,
            borderRadius: 8,
          }}
        />
        <Button
          variant="contained"
          sx={{
            px: { xs: 4, md: 10, width: "100%" },
            textTransform: "none",
            flexShrink: 0,
          }}
          onClick={methods.handleSubmit(handleSubmitSell)}
        >
          {Strings.button.sellMyCar}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Stack
      sx={{
        width: isMobile ? "100%" : isTab ? "80%" : "60%",
      }}
      alignSelf={"center"}
    >
      <Stack
        sx={{
          minHeight: 340,
          position: "relative",
          p: 2,
          borderRadius: "16px",
          overflow: "hidden",
          "::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.common.white,
            opacity: 0.8,
            zIndex: 0,
          },
        }}
      >
        <Stack
          sx={{ zIndex: 2, justifyContent: "center", alignItems: "center" }}
          mb={2}
        >
          <Typography
            sx={{ fontWeight: "bold", color: theme.palette.common.black }}
          >
            {Strings.common.ChooseYourIdealCar}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          gap={2}
          sx={{ zIndex: 2, justifyContent: "center" }}
          mb={3}
        >
          {[Strings.authPopup.burCars, Strings.authPopup.sellCars].map(
            (label, index) => (
              <Button
                key={label}
                onClick={() => setIsSelected(index)}
                sx={{
                  borderRadius: "30px",
                  px: 2,
                  backgroundColor:
                    isSelected === index
                      ? theme.palette.primary.main
                      : "transparent",
                  color:
                    isSelected === index
                      ? theme.palette.common.white
                      : theme.palette.common.black,
                }}
              >
                {label}
              </Button>
            ),
          )}
        </Stack>
        <FormProvider methods={methods}>
          {isSelected === 0 ? renderBuyCar() : renderSellCar()}
        </FormProvider>
      </Stack>
    </Stack>
  );
};

export { CarSearchBar };
